/**
 * Rutas de la SPA (Arquitectura §3.2: **una sola SPA**, tres áreas, code-splitting por
 * área).
 *
 * - `/login`, `/recuperar`, `/nueva-contrasena`: sin sesión, en el bundle inicial.
 * - `/admin/*`, `/profes/*`: detrás de `RequireAuth` y del gate de sede activa
 *   (RN-ROL-08); cada área es un chunk propio (`React.lazy`).
 * - `/kiosko/*` y `/tablet/*`: identidad de **dispositivo** (api key), no de usuario —
 *   chunk propio. `/tablet` es la URL fija de la tablet de recepción: instalable, siempre
 *   prendida y sin nada que la mande a `/login`.
 *
 * Las pantallas de cada área salen de `screens.ts`; acá sólo se decide el reparto por
 * área y las guardas. Un módulo nuevo NO toca este archivo: agrega su pantalla al
 * registro y su componente al mapa de su área (ver `web/README.md`).
 */
import { Suspense, lazy } from "react";
import { Navigate, createBrowserRouter } from "react-router";
import { useAuth } from "../lib/auth/index.js";
import { landingFor } from "./nav.js";
import { BootScreen, RequireActiveBranch, RequireAuth, SurfaceScope } from "./layout/Guards.js";
import LoginPage from "../features/auth/LoginPage.js";
import ForgotPasswordPage from "../features/auth/ForgotPasswordPage.js";
import ResetPasswordPage from "../features/auth/ResetPasswordPage.js";
import RouteError from "./pages/RouteError.js";
import TabletRouteError from "../features/tablet/TabletRouteError.js";
import NotFound from "./pages/NotFound.js";

const AdminArea = lazy(() => import("./areas/AdminArea.js"));
const ProfesArea = lazy(() => import("./areas/ProfesArea.js"));
const KioskoArea = lazy(() => import("./areas/KioskoArea.js"));
/** La tienda pública del kiosko: sin sesión, chunk propio (no arrastra el portal). */
const TiendaPage = lazy(() => import("../features/tienda/TiendaPage.js"));

/** `/` manda a cada rol a su home (el profe no entra al dashboard del dueño). */
function RootRedirect() {
  const { user } = useAuth();
  return <Navigate to={user ? landingFor(user.role) : "/login"} replace />;
}

function Area({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<BootScreen />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage />, errorElement: <RouteError /> },
  { path: "/recuperar", element: <ForgotPasswordPage />, errorElement: <RouteError /> },
  { path: "/nueva-contrasena", element: <ResetPasswordPage />, errorElement: <RouteError /> },

  // Tablet pública del kiosko: sin sesión ni dispositivo, la identidad es el token del link.
  {
    path: "/tienda/:token",
    errorElement: <RouteError />,
    element: (
      <Area>
        <TiendaPage />
      </Area>
    ),
  },

  // El kiosko NO pasa por `RequireAuth`: su identidad es la api key del dispositivo.
  {
    path: "/kiosko/*",
    errorElement: <RouteError />,
    element: (
      <SurfaceScope area="kiosko">
        <Area>
          <KioskoArea />
        </Area>
      </SurfaceScope>
    ),
  },
  /*
   * La tablet de recepción, con su URL fija (pedido de Damián, 21-oct-2026). Misma área,
   * sin `RequireAuth` (nunca redirige a /login: sólo usa la credencial del dispositivo) y
   * con un borde de error que se recarga solo — también si el chunk lazy no bajó.
   */
  {
    path: "/tablet/*",
    errorElement: <TabletRouteError />,
    element: (
      <SurfaceScope area="kiosko">
        <Area>
          <KioskoArea />
        </Area>
      </SurfaceScope>
    ),
  },

  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      { path: "/", element: <RootRedirect /> },
      {
        element: <RequireActiveBranch />,
        children: [
          {
            path: "/admin/*",
            element: (
              <SurfaceScope area="admin">
                <Area>
                  <AdminArea />
                </Area>
              </SurfaceScope>
            ),
          },
          {
            path: "/profes/*",
            element: (
              <SurfaceScope area="profes">
                <Area>
                  <ProfesArea />
                </Area>
              </SurfaceScope>
            ),
          },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
